"use client";

import { useEffect, useState } from "react";
import { DEMO_CTA_URL } from "@/lib/cta";

// Legora-inspired video hero (2026-06-05). SSR renders the poster image; the
// muted autoplay loop is swapped in on hydration for everyone EXCEPT
// reduced-motion and Save-Data users (mobile gets the loop too, like Legora).
// The poster stays the LCP element either way (fast first paint, no layout
// shift from the reserved min-height). Headline/copy are in the SSR HTML for
// SEO regardless of which media renders.
export function Hero() {
  const [showVideo, setShowVideo] = useState(false);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const saveData = (
      navigator as Navigator & { connection?: { saveData?: boolean } }
    ).connection?.saveData;
    // Upgrade poster → video after mount. Mobile gets the loop too (like
    // Legora); reduced-motion and Save-Data users keep the lightweight poster.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (!reduce && !saveData) setShowVideo(true);
  }, []);

  const mediaClass =
    "h-full w-full object-cover [filter:grayscale(100%)_contrast(1.03)]";

  return (
    <header className="relative flex min-h-[88vh] items-end overflow-hidden bg-background">
      <div className="absolute inset-0 z-0">
        {showVideo ? (
          <video
            className={mediaClass}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            poster="/hero-poster.jpg"
          >
            <source src="/hero-loop.mp4" type="video/mp4" />
          </video>
        ) : (
          // eslint-disable-next-line @next/next/no-img-element
          <img src="/hero-poster.jpg" alt="" className={mediaClass} />
        )}
      </div>

      {/* Legibility overlay — darkens toward the bottom where the copy sits. */}
      <div
        aria-hidden
        className="absolute inset-0 z-[1]"
        style={{
          background:
            "linear-gradient(180deg, rgba(12,14,16,0.5) 0%, rgba(12,14,16,0.2) 38%, rgba(12,14,16,0.9) 100%)",
        }}
      />

      <div className="relative z-[2] mx-auto w-full max-w-[1280px] px-6 pb-16 sm:px-10 sm:pb-20">
        <div className="mb-5 flex items-center gap-2.5 text-[13px] font-medium uppercase tracking-[0.08em] text-white/80">
          <span aria-hidden className="h-px w-6 bg-[#8FC9C2]" />
          Litigation intelligence
        </div>
        <h1 className="mb-6 max-w-[900px] font-sans text-[44px] font-medium leading-[1.03] tracking-[-0.022em] text-white sm:text-[56px] lg:text-[64px]">
          See the legal opportunity before it becomes a case.
        </h1>
        <p className="mb-5 max-w-[620px] text-[18px] leading-relaxed text-white/90 sm:text-[20px]">
          Some of the most valuable disputes begin quietly. A complaint pattern.
          A regulatory move. A data incident. A product failure. A market
          practice. A dispute that has not yet reached the courts.
        </p>
        <p className="mb-8 max-w-[560px] text-[16px] leading-relaxed text-white/75 sm:text-[17px]">
          Thexis helps law firms and litigation funders recognise those moments
          earlier, understand what matters, and decide whether there is a case
          worth building.
        </p>
        <div className="flex flex-wrap items-center gap-5">
          <a
            href={DEMO_CTA_URL}
            className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3.5 font-sans text-[15px] font-medium text-accent transition-colors hover:bg-[#f0efe9]"
          >
            Request early access
            <span
              aria-hidden
              className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-accent/15 text-xs"
            >
              →
            </span>
          </a>
          <span className="max-w-[380px] text-[13.5px] leading-relaxed text-white/65">
            For teams working on complex disputes, collective actions, funded
            cases and high-value legal claims.
          </span>
        </div>
      </div>
    </header>
  );
}
